import React from 'react';
import {
  Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis,
} from 'recharts';
import cn from '@/utils/cn';
import type { LiveSessionsMetrics, SessionsStartedPerHour, StudentsPerSession } from './types';

interface ChartsProps {
  metrics: LiveSessionsMetrics | undefined;
  onSelectSession: (sessionId: string) => void;
}

const BAR_COLOR = '#522e91';

const ChartCard = ({ children, title }: { children: React.ReactNode; title: string }) => (
  <div className="tw-bg-white tw-border tw-border-solid tw-border-gray-200 tw-rounded-lg tw-p-5 tw-shadow-sm">
    <h2 className="tw-text-sm tw-font-semibold tw-text-gray-900 tw-mt-0 tw-mb-4">{title}</h2>
    {children}
  </div>
);

const EmptyState = ({ message }: { message: string }) => (
  <div className="tw-flex tw-items-center tw-justify-center tw-h-64 tw-text-sm tw-text-gray-400">
    {message}
  </div>
);

interface ConnectionBucket {
  count: number;
  label: string;
}

const RANGE_BUCKETS: { label: string; max: number }[] = [
  { label: '<25%', max: 0.25 },
  { label: '25–49%', max: 0.5 },
  { label: '50–74%', max: 0.75 },
  { label: '75–99%', max: 1 },
];

const buildConnectionBuckets = (sessions: StudentsPerSession[]): ConnectionBucket[] => {
  const buckets: ConnectionBucket[] = RANGE_BUCKETS.map(b => ({ count: 0, label: b.label }));
  buckets.push({ count: 0, label: '100%' });

  sessions.forEach((s) => {
    if (s.total === 0) return;
    const rate = s.connected / s.total;
    if (rate >= 1) {
      buckets[buckets.length - 1].count += 1;
      return;
    }
    const idx = RANGE_BUCKETS.findIndex(b => rate < b.max);
    if (idx !== -1) buckets[idx].count += 1;
  });

  return buckets;
};

interface OutlierRow extends StudentsPerSession {
  label: string;
  rate: number;
}

const OUTLIER_LIMIT = 5;

const buildOutliers = (sessions: StudentsPerSession[]): OutlierRow[] => sessions
  .filter(s => s.total > 0 && s.connected < s.total)
  .map(s => ({
    ...s,
    label: s.teacher_name ?? 'Unknown teacher',
    rate: s.connected / s.total,
  }))
  .sort((a, b) => a.rate - b.rate)
  .slice(0, OUTLIER_LIMIT);

const severityClass = (rate: number): string => {
  if (rate < 0.5) return 'tw-bg-red-50 tw-text-red-700';
  if (rate < 0.75) return 'tw-bg-amber-50 tw-text-amber-700';
  return 'tw-bg-gray-100 tw-text-gray-700';
};

const OutlierItem = ({ onSelect, row }: { onSelect: () => void; row: OutlierRow }) => (
  <button
    className={cn(
      'tw-w-full tw-flex tw-items-center tw-justify-between tw-gap-3',
      'tw-py-3 tw-px-3 tw-rounded-md tw-bg-transparent tw-border-0',
      'tw-text-left tw-cursor-pointer hover:tw-bg-gray-50',
      'focus-visible:tw-outline focus-visible:tw-outline-2 focus-visible:tw-outline-tci-purple',
    )}
    type="button"
    onClick={onSelect}
  >
    <span className="tw-text-sm tw-font-medium tw-text-gray-900 tw-truncate">
      {row.label}
    </span>
    <span className="tw-flex tw-items-center tw-gap-2 tw-whitespace-nowrap">
      <span className="tw-text-xs tw-text-gray-500 tw-tabular-nums">
        {row.connected} / {row.total}
      </span>
      <span
        className={cn(
          'tw-text-xs tw-font-semibold tw-px-2 tw-py-0.5 tw-rounded tw-tabular-nums',
          severityClass(row.rate),
        )}
      >
        {Math.round(row.rate * 100)}%
      </span>
    </span>
  </button>
);

interface StartedRow extends SessionsStartedPerHour {
  label: string;
}

const formatHourLabel = (iso: string): string => {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleTimeString([], { hour: 'numeric' });
};

const buildStartedRows = (buckets: SessionsStartedPerHour[]): StartedRow[] => buckets.map(b => ({
  ...b,
  label: formatHourLabel(b.hour),
}));

const Charts = ({ metrics, onSelectSession }: ChartsProps) => {
  if (!metrics) return null;

  const hasSessions = metrics.students_per_session.some(s => s.total > 0);
  const connectionBuckets = buildConnectionBuckets(metrics.students_per_session);
  const outliers = buildOutliers(metrics.students_per_session);
  const startedRows = buildStartedRows(metrics.sessions_started_per_hour ?? []);
  const startedTotal = startedRows.reduce((sum, row) => sum + row.count, 0);

  return (
    <div className="tw-flex tw-flex-col tw-gap-4">
      <div className="tw-grid tw-grid-cols-1 lg:tw-grid-cols-2 tw-gap-4">
        <ChartCard title="Active sessions by program">
          {metrics.sessions_by_program.length === 0 ? (
            <EmptyState message="No active sessions" />
          ) : (
            <ResponsiveContainer height={260} width="100%">
              <BarChart
                data={metrics.sessions_by_program}
                layout="vertical"
                margin={{ bottom: 0, left: 20, right: 20, top: 10 }}
              >
                <CartesianGrid horizontal={false} stroke="#f3f4f6" />
                <XAxis allowDecimals={false} stroke="#6b7280" type="number" />
                <YAxis
                  dataKey="program"
                  stroke="#6b7280"
                  tick={{ fontSize: 12 }}
                  type="category"
                  width={140}
                />
                <Tooltip />
                <Bar dataKey="count" fill={BAR_COLOR} radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </ChartCard>

        <ChartCard title="Connection rate distribution">
          {!hasSessions ? (
            <EmptyState message="No active sessions" />
          ) : (
            <ResponsiveContainer height={260} width="100%">
              <BarChart
                data={connectionBuckets}
                margin={{ bottom: 0, left: 0, right: 20, top: 10 }}
              >
                <CartesianGrid stroke="#f3f4f6" vertical={false} />
                <XAxis
                  dataKey="label"
                  interval={0}
                  stroke="#6b7280"
                  tick={{ fontSize: 11 }}
                />
                <YAxis allowDecimals={false} stroke="#6b7280" />
                <Tooltip />
                <Bar dataKey="count" fill={BAR_COLOR} radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </ChartCard>
      </div>

      <div className="tw-grid tw-grid-cols-1 lg:tw-grid-cols-2 tw-gap-4">
        <ChartCard title="Sessions with low attendance">
          {!hasSessions && <EmptyState message="No active sessions" />}
          {hasSessions && outliers.length === 0 && (
            <EmptyState message="All active sessions at full attendance." />
          )}
          {hasSessions && outliers.length > 0 && (
            <ul className="tw-flex tw-flex-col tw-gap-1 tw-list-none tw-p-0 tw-m-0">
              {outliers.map(row => (
                <li key={row.session_id}>
                  <OutlierItem row={row} onSelect={() => onSelectSession(row.session_id)} />
                </li>
              ))}
            </ul>
          )}
        </ChartCard>

        <ChartCard title="Sessions started per hour (US school day)">
          {startedTotal === 0 ? (
            <EmptyState message="No sessions started during today's school hours" />
          ) : (
            <ResponsiveContainer height={260} width="100%">
              <BarChart
                data={startedRows}
                margin={{ bottom: 0, left: 0, right: 20, top: 10 }}
              >
                <CartesianGrid stroke="#f3f4f6" vertical={false} />
                <XAxis
                  dataKey="label"
                  interval={0}
                  stroke="#6b7280"
                  tick={{ fontSize: 11 }}
                />
                <YAxis allowDecimals={false} stroke="#6b7280" />
                <Tooltip />
                <Bar dataKey="count" fill={BAR_COLOR} radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </ChartCard>
      </div>
    </div>
  );
};

export default Charts;
